import { useEffect, useState } from 'react';

import { usePageContext } from '~components/PageDataProvider/PageDataProvider';
import PersonalCodeForm from '~components/PersonalCodeForm/PersonalCodeForm';
import Section from '~components/_layout/Section';
import { addUrlParam, getPpccParamValueByNameAndPage } from '~globals/common';
import useFeatureFlags from '~hooks/featureFlags/useFeatureFlags';
import useAllianceData from '~hooks/useAllianceData';
import { useAppSelector } from '~hooks/useRedux';
import HeroImage from '~images/personal-code/hero-desktop.png';

export default function PersonalCodeHero() {
    const { ppccPageCode } = usePageContext();

    const allianceId = useAppSelector((state) => state.global.allianceId);
    const personalCodeData = useAppSelector((state) => state.display.personalCode);
    const zipcode = useAppSelector((state) => state.global.geoLocation.zip);

    const [allianceResponse, status] = useAllianceData(allianceId);
    const [ehmpUrl, setEhmpUrl] = useState('');

    useEffect(
        function pcUseEffect() {
            let url = `${process.env.GATSBY_EHMP_URL}ehi/medicare/fast-quote`;
            const urlParams = new URLSearchParams(window.location.search);
            const planType = urlParams.get('type') || 'MA';
            const ehmpTarget = urlParams.get('target') || 'medicare-shopping';
            url = addUrlParam(url, 'allid', allianceId);
            url = addUrlParam(url, 'type', planType);
            url = addUrlParam(url, 'target', ehmpTarget);
            if (zipcode) {
                url = addUrlParam(url, 'zip', zipcode);
            }
            setEhmpUrl(url);
        },
        [zipcode, allianceId]
    );

    if (status === 'loading') {
        return null;
    }

    if (status === 'error') {
        console.log('allID loading error');
        return null;
    }

    if (!allianceResponse.partnerAllianceInfo) {
        console.log('allID did not return partner data');
        return null;
    }

    const pageConfigs = allianceResponse.partnerAllianceInfo.landingPageConfigs || [];

    const { partnerDisplayName } = allianceResponse.partnerAllianceInfo.allianceData;

    const header = getPpccParamValueByNameAndPage({
        pageCode: ppccPageCode,
        pageConfigs,
        paramName: 'header',
    }) ?? (
        <>
            Compare the nation’s top plans<span className="text-16 align-super">*</span>
        </>
    );

    const subHeader = getPpccParamValueByNameAndPage({
        pageCode: ppccPageCode,
        pageConfigs,
        paramName: 'sub_header',
    }) ?? (
        <>
            Compare plans with your doctors and prescriptions anonymously
            {partnerDisplayName ? ' provided by ' + partnerDisplayName : ''} – all in one place.
            <br className="hidden lg:block" /> Enter your:
        </>
    );

    const heroImage =
        (getPpccParamValueByNameAndPage({
            pageCode: ppccPageCode,
            pageConfigs,
            paramName: 'hero_image_url',
        }) as string) ?? HeroImage;

    const { ehim_providenceCompliance } = useFeatureFlags().ProvidenceCompliance();
    const enableProvidenceCompliance = ehim_providenceCompliance.enable;

    return (
        <Section extraClasses="bg-surface-0 p-4 mb-8 md:p-6 xl:pt-12 xl:pb-0">
            <div className="max-w-[1440px] mx-auto">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-8 md:gap-6 xl:gap-x-6 xl:gap-y-0 xl:grid-cols-12 max-w-screen-xl mx-auto">
                    <div className="col-span-full xl:col-span-8">
                        <h1 className="font-bold text-center md:text-left text-2xl md:text-3xl xl:text-4xl">
                            {header}
                        </h1>
                    </div>
                    <div className="col-span-full xl:col-span-8">
                        <p className="text-18 leading-28 font-bold text-center md:text-left">
                            Securely find your Medicare match with PlanMatch.
                        </p>
                        <p className="text-sm md:text-lg text-center md:text-left">{subHeader}</p>
                    </div>
                    <div className="hidden md:block md:col-span-3 xl:row-start-1 xl:row-span-4 xl:col-span-4 xl:relative mx-auto">
                        <div className="block relative">
                            <img src={heroImage} alt="" />
                        </div>
                    </div>
                    <div className="md:col-span-5 xl:col-span-8">
                        <PersonalCodeForm redesign={true} />
                    </div>
                    <div className="text-center md:text-left md:col-span-6 xl:col-span-8 xl:mb-6">
                        {enableProvidenceCompliance ? (
                            <p
                                className={
                                    personalCodeData.errorType === 'maxAttempts'
                                        ? 'text-red'
                                        : 'text-black'
                                }
                            >
                                {personalCodeData.errorType === 'maxAttempts' ? (
                                    <span className="text-xl" aria-live="polite">
                                        <span className="font-bold">
                                            You&apos;ve exceeded your allowed entry attempts.
                                        </span>
                                        You can still see Medicare Plans in your area without your
                                        {' PlanMatch Code. '}
                                        <a href={ehmpUrl} className="text-primary font-bold">
                                            Click here
                                        </a>
                                        {' to get started.'}
                                    </span>
                                ) : (
                                    <span className="text-xl">
                                        Or{' '}
                                        <a
                                            href={ehmpUrl}
                                            className="underline text-primary"
                                            data-wa-link="compare plan"
                                        >
                                            compare plans
                                        </a>{' '}
                                        without using your doctor and prescription information.
                                    </span>
                                )}
                            </p>
                        ) : (
                            <p
                                className={`text-lg ${
                                    personalCodeData.errorType === 'maxAttempts'
                                        ? 'text-red'
                                        : 'text-black'
                                }`}
                            >
                                {personalCodeData.errorType === 'maxAttempts' ? (
                                    <span className="font-bold" aria-live="polite">
                                        You&apos;ve exceeded your allowed entry attempts
                                    </span>
                                ) : (
                                    <span className="text-xl">
                                        Or{' '}
                                        <a
                                            href={ehmpUrl}
                                            className="underline text-primary"
                                            data-wa-link="compare plan"
                                        >
                                            compare plans
                                        </a>{' '}
                                        without using your doctor and prescription information.
                                    </span>
                                )}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </Section>
    );
}
